import React, { Component } from 'react';
import { connect } from 'react-redux';
import { get, isEmpty, isEqual } from 'lodash';

import asyncRoute from '../../helpers/hoc/asyncRoute';
import routes from '../../routes';
import {
  initiatePaymentAction,
  verifyPaymentAction,
  clearApiErrorResponseAction,
  dispatchLastCashCardRechargeTransactionAction,
  clearCashCardRechargeAction,
} from '../../actions/app.action';
import { getErrorTitle, getErrorDescription, getButtonHandlerCta } from '../../selectors';
import { fibonacciPolling, getOnlinePaymentTypeConfig, isUUID } from '../../utils/helpers';

import { constants } from '../../constants/configuration';

import {
  customErrorCodes,
  paymentPortalGuestID,
  apiResponseCodes,
  onlinePaymentTypesConfig,
  cashfreeEnvironment,
} from '../../constants/globals';
import themeVariables from '../../style/color-export.module.scss';

import paymentFailedImg from '../../assets/images/CrossRed.svg';
import paymentSuccessImg from '../../assets/images/Success.svg';

import './Payment.scss';

const Button = asyncRoute(() => import('../../components/MaterialElements/Button'));
const CircularProgressBar = asyncRoute(() => import('../../components/CircularProgressBar'));

const paymentScreenConfig = constants.payment;
const paymentProcessingMaxTimeInSeconds = 180;

const primaryThemeColor = themeVariables.primaryThemeColor;
const primaryTextColor = themeVariables.primaryTextColor;
class PaymentV1 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      handleButtonCta: () => {},
      errorDescription: '',
      showPaymentProcessing: false,
      showPaymentError: false,
      showPaymentSuccess: false,
    };

    this.timerInstance = {
      currentIntervalIndex: 0,
      remainingSeconds: paymentProcessingMaxTimeInSeconds,
    };

    const URLQueryParams = new URLSearchParams(props.location.search);

    const onlinePaymentTypeQueryParam = (URLQueryParams && URLQueryParams.get('type')) || '';
    const onlinePaymentTypeConfig = getOnlinePaymentTypeConfig(
      'queryParam',
      onlinePaymentTypeQueryParam,
    );

    const onlinePaymentType = onlinePaymentTypeConfig.code;

    const initiatorRouteFromQueryParams =
      (URLQueryParams && URLQueryParams.get('initiatorRoute')) || '';
    // We receive order id in 'id' query param in case of order payment flow
    const orderIdFromQueryParams =
      (onlinePaymentType === onlinePaymentTypesConfig.orderPayment.code &&
        URLQueryParams &&
        URLQueryParams.get('id')) ||
      '';
    // We receive cash card recharge txn id in 'id' query param in case of cash card recharge or cash card recharge plus order payment flow
    const cashCardRechargeId =
      ([
        onlinePaymentTypesConfig.cashCardRecharge.code,
        onlinePaymentTypesConfig.orderPaymentPlusCashCardRecharge.code,
      ].includes(onlinePaymentType) &&
        URLQueryParams &&
        URLQueryParams.get('id')) ||
      '';
    const backFromPaymentPortal = URLQueryParams && !!URLQueryParams.get('isCallback'); // Check if the 'type' query parameter is present in the url. If so, we have landed back to our webapp from the payment portal.

    // If the user has returned to webapp from the payment portal and abruptly refreshes the page, verify the payment
    if (backFromPaymentPortal && window.performance && performance.navigation.type === 1) {
      console.log('verify payment called inside constructor');
      this.verifyPayment(onlinePaymentType);
    }

    this.backFromPaymentPortal = backFromPaymentPortal;
    this.onlinePaymentTypeAfterPayment = onlinePaymentType;
    this.initiatorRoute = initiatorRouteFromQueryParams;
    this.orderId = orderIdFromQueryParams;
    this.cashCardRechargeId = cashCardRechargeId;
    this.redirectToCart = this.redirectToCart.bind(this);
    this.paymentOrderId = orderIdFromQueryParams || get(props, 'orderedItems.id', '');
  }

  componentDidMount() {
    const { history } = this.props;
    const onlinePaymentTypeBeforePayment = get(
      history,
      'location.state.onlinePaymentType',
      this.onlinePaymentTypeAfterPayment,
    );
    const initiatorRoute = get(history, 'location.state.initiatorRoute', this.initiatorRoute);
    const isNotValidPaymentFlow = !onlinePaymentTypeBeforePayment;
    if (isNotValidPaymentFlow) {
      this.setState({
        showErrorPopup: true,
        errorContent: {
          // @ts-ignore
          title: getErrorTitle({ code: customErrorCodes.contextlessURLHit }),
          // @ts-ignore
          description: getErrorDescription({ code: customErrorCodes.contextlessURLHit }),
          // @ts-ignore
          buttonCta: getButtonHandlerCta({ code: customErrorCodes.contextlessURLHit }),
        },
        handleButtonCta: this.handleContextlessURLHitErrorPopupCTA,
      });
    }
    if (!this.backFromPaymentPortal) {
      // If online payment type has been set before payment, initiate a payment
      const paymentInitiationCheck = !!onlinePaymentTypeBeforePayment;

      // If all payment initiation checks are met, initiate payment
      if (paymentInitiationCheck) {
        this.onInitiatePayment(onlinePaymentTypeBeforePayment, initiatorRoute);
      } else {
        this.goForPaymentVeriifciation();
      }
    } else {
      this.goForPaymentVeriifciation();
    }

    this.props.clearApiErrorResponseAction();
  }

  componentWillReceiveProps(nextProps) {
    if (
      !isEmpty(get(nextProps, 'errorResponse')) &&
      !isEqual(get(this.props, 'errorResponse'), get(nextProps, 'errorResponse'))
    ) {
      const { code, message } = nextProps.errorResponse;

      this.setState(
        {
          showErrorPopup: true,
          errorContent: {
            title: getErrorTitle({ source: paymentScreenConfig, code }),
            description: getErrorDescription({ source: paymentScreenConfig, code, message }),
            buttonCta: this.getPaymentErrorCTAText(),
          },
          handleButtonCta: this.navigationAfterPaymentError,
        },
        () => {
          this.props.clearApiErrorResponseAction();
        },
      );
    }
  }

  componentWillUnmount() {
    this.setState({
      showErrorPopup: false,
      errorContent: {},
    });
  }

  getPaymentErrorCTAText = () => {
    const orderFlowCheck =
      this.onlinePaymentTypeAfterPayment === onlinePaymentTypesConfig.orderPayment.code;
    const cashCardRechargeFlowCheck =
      this.onlinePaymentTypeAfterPayment === onlinePaymentTypesConfig.cashCardRecharge.code;
    const cashCardRechargePlusOrderPaymentFlowCheck =
      this.onlinePaymentTypeAfterPayment ===
      onlinePaymentTypesConfig.orderPaymentPlusCashCardRecharge.code;

    if (orderFlowCheck || cashCardRechargePlusOrderPaymentFlowCheck) {
      return paymentScreenConfig.backToCartCTAText;
    }

    if (cashCardRechargeFlowCheck) {
      return paymentScreenConfig.backCTAText;
    }
  };

  navigationAfterPaymentError = () => {
    const orderFlowCheck =
      this.onlinePaymentTypeAfterPayment === onlinePaymentTypesConfig.orderPayment.code;
    const cashCardRechargeFlowCheck =
      this.onlinePaymentTypeAfterPayment === onlinePaymentTypesConfig.cashCardRecharge.code;
    const cashCardRechargePlusOrderPaymentFlowCheck =
      this.onlinePaymentTypeAfterPayment ===
      onlinePaymentTypesConfig.orderPaymentPlusCashCardRecharge.code;

    if (orderFlowCheck || cashCardRechargePlusOrderPaymentFlowCheck) {
      cashCardRechargePlusOrderPaymentFlowCheck && this.props.clearCashCardRecharge();

      this.redirectToCart();
      return;
    }

    if (cashCardRechargeFlowCheck) {
      this.props.clearCashCardRecharge();

      this.setState(
        {
          showErrorPopup: false,
          errorContent: {},
        },
        () => {
          this.props.history.push(this.initiatorRoute, { from: routes.containers.payment });
        },
      );
    }
  };

  handleContextlessURLHitErrorPopupCTA = () => {
    // Reload and open webapp's home
    window.location.replace(window.location.origin);
  };

  verifyPayment = (onlinePaymentType) => {
    const { defaultGateway } = this.props;
    const { id } = this.getVerifyPaymentAPIRequestProps(onlinePaymentType);

    const verifyPaymentReqBody = {
      type: onlinePaymentType,
      id,
    };

    this.setState(
      {
        showErrorPopup: false,
        errorContent: {},
      },
      () => {
        this.props
          .verifyPaymentAction({ verifyPaymentReqBody, defaultGateway })
          .then((response) => {
            if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
              this.validatePaymentReponse(response);
            }
          });
      },
    );
  };

  redirectToCart = () => {
    this.setState(
      {
        showErrorPopup: false,
        errorContent: {},
      },
      () => {
        this.props.history.push(routes.containers.cart);
      },
    );
  };

  onInitiatePayment = (onlinePaymentType, initiatorRoute) => {
    const { defaultGateway, loginData } = this.props;
    const guestId = get(loginData, 'id', '');
    const guestMobileNumber = get(loginData, 'primaryPhoneNumber', '');
    let initiatePaymentReqBody = {};

    const isRechargeAndOrderFlow =
      onlinePaymentType === onlinePaymentTypesConfig.orderPaymentPlusCashCardRecharge.code;

    const { id, payableAmount } = this.getInitiatePaymentAPIRequestProps(onlinePaymentType);

    const callbackUrl = `${window.location.origin}/${routes.containers.payment}`;

    const onlinePaymentTypeConfig = getOnlinePaymentTypeConfig('code', onlinePaymentType);

    initiatePaymentReqBody = {
      type: isRechargeAndOrderFlow // In case of recharge and order flow, send type as recharge
        ? onlinePaymentTypesConfig.cashCardRecharge.code
        : onlinePaymentType,
      id, // id of the entity for which the payment is being made
      amount: payableAmount, // get payable amount from url query params, fallback to payableAmount obtained above
      guestId: guestId || paymentPortalGuestID, // get guest id from url query params, fallback to guest id from login data
      ...(guestMobileNumber ? { mobileNo: guestMobileNumber } : {}),
      callbackUrl,
      callbackParams: {
        isCallback: true, // As this data is meant for the flow after the guest is back from payment portal, isCallback (backFromPaymentPortal) should be true at that point
        type: onlinePaymentTypeConfig.queryParam,
        initiatorRoute,
        id,
      },
    };

    this.props
      .initiatePaymentAction({ initiatePaymentReqBody, defaultGateway })
      .then((initiatePaymentResponse) => {
        if (apiResponseCodes.SUCCESS.includes(get(initiatePaymentResponse, 'status', ''))) {
          let checkoutOptions = {
            paymentSessionId: get(
              initiatePaymentResponse,
              'data.paymentRequestBody.paymentSessionId',
              '',
            ),
            returnUrl: get(initiatePaymentResponse, 'data.paymentRequestBody.returnUrl', ''),
          };
          const cashfree = window.Cashfree({ mode: cashfreeEnvironment });

          cashfree.checkout(checkoutOptions).then((result) => {
            if (result.redirect) {
              console.log('Redirection');
            }
          });
        }
      });
  };

  navigateAsPerType = async ({
    orderFlowCheck,
    cashCardRechargeFlowCheck,
    cashCardRechargePlusOrderPaymentFlowCheck,
    response,
  }) => {
    orderFlowCheck && this.navigateToOrderConfirmationAfterPayment();
    if (cashCardRechargeFlowCheck || cashCardRechargePlusOrderPaymentFlowCheck) {
      const guestCashCardTransaction = get(response, 'data.cashCardTransaction', {});
      await this.props.dispatchLastCashCardRechargeTransaction(guestCashCardTransaction);
      const guestCashCardId = get(guestCashCardTransaction, 'guestCashCardId', '');
      const linkedOrderId = get(guestCashCardTransaction, 'linkedPaymentId', '');
      this.navigateToRechargeConfirmationAfterPayment(
        guestCashCardId,
        cashCardRechargePlusOrderPaymentFlowCheck,
        linkedOrderId,
      );
    }
  };

  validatePaymentReponse = async (response) => {
    const orderFlowCheck =
      this.onlinePaymentTypeAfterPayment === onlinePaymentTypesConfig.orderPayment.code;
    const cashCardRechargeFlowCheck =
      this.onlinePaymentTypeAfterPayment === onlinePaymentTypesConfig.cashCardRecharge.code;
    const cashCardRechargePlusOrderPaymentFlowCheck =
      this.onlinePaymentTypeAfterPayment ===
      onlinePaymentTypesConfig.orderPaymentPlusCashCardRecharge.code;

    if (get(response, 'data.status')) {
      if (isEqual(get(response, 'data.transaction.status'), 'SUCCESS')) {
        this.setState(
          {
            showPaymentSuccess: true,
          },
          () => {
            setTimeout(
              () =>
                this.navigateAsPerType({
                  orderFlowCheck,
                  cashCardRechargeFlowCheck,
                  cashCardRechargePlusOrderPaymentFlowCheck,
                  response,
                }),
              1000,
            );
          },
        );
      }
    } else {
      if (isEqual(get(response, 'data.guestOrderDto.statusCode'), 'DRAFT')) {
        this.setState({
          showPaymentProcessing: true,
        });
        // Calculate next interval based on the current index
        const getNextPollInterval = fibonacciPolling(paymentProcessingMaxTimeInSeconds);
        const nextIndex = get(this.timerInstance, 'current.currentIntervalIndex', 0) + 1;
        this.timerInstance.current = {
          currentIntervalIndex: nextIndex,
          remainingSeconds: get(this.timerInstance, 'current.remainingSeconds'),
        };
        if (
          nextIndex < getNextPollInterval.length &&
          this.timerInstance.current.remainingSeconds > 1
        ) {
          // Update current index
          const nextInterval = getNextPollInterval[nextIndex];
          setTimeout(
            () => this.verifyPayment(this.onlinePaymentTypeAfterPayment),
            nextInterval * 1000,
          ); // Convert seconds to milliseconds
        } else {
          localStorage.removeItem(`timerInstance-${this.paymentOrderId}`);
          this.setState({
            showPaymentProcessing: false,
            showPaymentError: true,
          });
        }
      } else {
        let transactionRemark = get(response, 'data.transaction.remark', '');
        this.setState({
          showPaymentProcessing: false,
          showPaymentError: true,
          errorDescription: !isUUID(transactionRemark) ? transactionRemark : '',
        });
      }
    }

    // orderFlowCheck && this.navigateToOrderConfirmationAfterPayment();
    // if (cashCardRechargeFlowCheck || cashCardRechargePlusOrderPaymentFlowCheck) {
    //   const guestCashCardTransaction = get(response, 'data.cashCardTransaction', {});
    //   await this.props.dispatchLastCashCardRechargeTransaction(guestCashCardTransaction);
    //   const guestCashCardId = get(guestCashCardTransaction, 'guestCashCardId', '');
    //   const linkedOrderId = get(guestCashCardTransaction, 'linkedPaymentId', '');
    //   this.navigateToRechargeConfirmationAfterPayment(
    //     guestCashCardId,
    //     cashCardRechargePlusOrderPaymentFlowCheck,
    //     linkedOrderId,
    //   );
    // }
  };

  navigateToOrderConfirmationAfterPayment = async () => {
    const { history, orderedItems } = this.props;

    const orderId = this.orderId || get(orderedItems, 'id', '');

    history &&
      orderId &&
      history.push(`/${routes.containers.confirmation}/${orderId}`, {
        initiatorRoute: `/${routes.containers.confirmation}`,
      });
  };

  navigateToRechargeConfirmationAfterPayment = (
    guestCashCardId,
    cashCardRechargePlusOrderPaymentFlowCheck,
    linkedOrderId,
  ) => {
    this.props.clearCashCardRecharge();

    const { history } = this.props;

    // If payment initiator route has been set and it not a recharge and order payment flow, navigate to it, else navigate to guest cash card detail as a fallback
    if (!!this.initiatorRoute && !cashCardRechargePlusOrderPaymentFlowCheck) {
      history && history.push(this.initiatorRoute, { from: routes.containers.payment });
    } else {
      history &&
        history.push(`/${routes.containers.cashCards}/${guestCashCardId}`, {
          from: `/${routes.containers.payment}`,
          ...(cashCardRechargePlusOrderPaymentFlowCheck ? { linkedOrderId } : {}),
        });
    }
  };

  getInitiatePaymentAPIRequestProps = (onlinePaymentType) => {
    switch (onlinePaymentType) {
      case onlinePaymentTypesConfig.orderPaymentPlusCashCardRecharge.code:
      case onlinePaymentTypesConfig.cashCardRecharge.code: {
        const { guestCashCardRechargeDetails } = this.props;
        const id = get(guestCashCardRechargeDetails, 'cashCardTransaction.id', '');
        const payableAmount = get(guestCashCardRechargeDetails, 'cashCardTransaction.amount');
        return { id, payableAmount };
      }
      case onlinePaymentTypesConfig.orderPayment.code: {
        const { orderedItems } = this.props;
        const { id, totalAmount, netBalance } = orderedItems;
        const payableAmount = netBalance || totalAmount; // If payment has partly been done through loyalty points e.g. cash card balance, then only rhe remainder is payable online, otherwise the total order amount is payable online
        return { id, payableAmount };
      }
      default:
        return { id: '', payableAmount: 0 };
    }
  };

  getVerifyPaymentAPIRequestProps = (onlinePaymentType) => {
    switch (onlinePaymentType) {
      case onlinePaymentTypesConfig.orderPaymentPlusCashCardRecharge.code:
      case onlinePaymentTypesConfig.cashCardRecharge.code: {
        const { guestCashCardRechargeDetails } = this.props;
        const id =
          this.cashCardRechargeId ||
          get(guestCashCardRechargeDetails, 'cashCardTransaction.id', '');
        return { id };
      }
      case onlinePaymentTypesConfig.orderPayment.code: {
        const { orderedItems } = this.props;
        const id = this.orderId || get(orderedItems, 'id', '');
        return { id };
      }
      default:
        return { id: '' };
    }
  };

  goForPaymentVeriifciation = () => {
    let timerInstanceFromLocal = localStorage.getItem(`timerInstance-${this.paymentOrderId}`);

    timerInstanceFromLocal = timerInstanceFromLocal && JSON.parse(timerInstanceFromLocal);

    if (!isEmpty(timerInstanceFromLocal)) {
      this.timerInstance.current = timerInstanceFromLocal;
    } else {
      this.timerInstance.current = {
        currentIntervalIndex: 0,
        remainingSeconds: paymentProcessingMaxTimeInSeconds,
      };
    }
    this.verifyPayment(
      isEqual(this.onlinePaymentTypeAfterPayment, 'RECH_PLUS_PAYMENT')
        ? 'RECH'
        : this.onlinePaymentTypeAfterPayment,
    );
  };

  handleRemainingSecondsChange = (remainingSeconds) => {
    if (remainingSeconds > 0) {
      this.timerInstance.current = {
        currentIntervalIndex: get(this.timerInstance, 'current.currentIntervalIndex', 0),
        remainingSeconds: remainingSeconds,
      };
      localStorage.setItem(
        `timerInstance-${this.paymentOrderId}`,
        JSON.stringify(get(this.timerInstance, 'current')),
      );
    } else {
      localStorage.removeItem(`timerInstance-${this.paymentOrderId}`);
      this.setState({
        showPaymentProcessing: false,
        showPaymentError: true,
      });
    }
  };

  render() {
    const { showPaymentProcessing, showPaymentError, showPaymentSuccess, errorDescription } =
      this.state;
    return (
      <div className="payment">
        <div className="payment_section">
          {showPaymentProcessing && (
            <React.Fragment>
              <CircularProgressBar
                themeColor={primaryThemeColor}
                textColor={primaryTextColor}
                maxTimeInSeconds={paymentProcessingMaxTimeInSeconds}
                secondsRemaining={get(this.timerInstance, 'current.remainingSeconds')}
                paymentOrderId={this.paymentOrderId}
                onRemainingSecondsChange={(remainingSeconds) =>
                  this.handleRemainingSecondsChange(remainingSeconds)
                }
              />
              <h2>{paymentScreenConfig.processingPayment.title}</h2>
              <div>{paymentScreenConfig.processingPayment.description}</div>
            </React.Fragment>
          )}
          {showPaymentError && (
            <React.Fragment>
              <img src={paymentFailedImg} alt="payment-failed" />
              <h2>{paymentScreenConfig.paymentFailed}</h2>
              <div className="payment_section_description">
                {paymentScreenConfig.paymentFailedDescription}
                {errorDescription && (
                  <>
                    <br />
                    {errorDescription}
                  </>
                )}
              </div>
              <Button
                variant="outlined"
                content={paymentScreenConfig.backToCartCTA}
                onClick={() => this.redirectToCart()}
              />
            </React.Fragment>
          )}
          {showPaymentSuccess && (
            <React.Fragment>
              <img src={paymentSuccessImg} alt="payment-success" />
              <h2>{paymentScreenConfig.paymentSuccess}</h2>
            </React.Fragment>
          )}
        </div>
      </div>
    );
  }
}

export default connect(
  (state) => ({
    loginData: get(state, 'authentication.loginData', {}),
    orderedItems: get(state, 'app.orderedItems', {}),
    guestCashCardRechargeDetails: get(state, 'app.guestCashCardRechargeDetails', {}),
    defaultGateway: get(state, 'app.restaurant.restaurantConfig.defaultGateway', 'C'), // set defaultGateway as Cashfree
    errorResponse: get(state, 'app.errorResponse', {}),
  }),
  {
    initiatePaymentAction,
    verifyPaymentAction,
    clearApiErrorResponseAction,
    dispatchLastCashCardRechargeTransaction: dispatchLastCashCardRechargeTransactionAction,
    clearCashCardRecharge: clearCashCardRechargeAction,
  },
)(PaymentV1);
